import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, PlusIcon, GripVerticalIcon, PencilIcon, Trash2Icon, AlertTriangleIcon, FolderIcon, CheckIcon } from 'lucide-react';

/** Every item without a merchant category lands here automatically. */
export const UNCATEGORIZED = 'Uncategorized';

/* ------------------------------------------------------------------ shell */

function Sheet({
  title,
  subtitle,
  onClose,
  children,
  wide
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-1021" data-mp="3rh80huac65a7">
      <div className="absolute inset-0 bg-dark-navy/40 backdrop-blur-sm" onClick={onClose} data-id="element-1022" data-mp="3vibhik44e7d0" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.18,
      ease: [0.23, 1, 0.32, 1]
    }} className={`relative w-full ${wide ? 'max-w-lg' : 'max-w-md'} bg-white rounded-3xl shadow-large overflow-hidden max-h-[88vh] flex flex-col`} data-id="element-1023" data-mp="1potm35703iyj">
        <div className="flex items-start gap-4 px-7 pt-6 pb-5 border-b border-slate-100" data-id="element-1024" data-mp="88er00zu1h0c">
          <div className="min-w-0 flex-1" data-id="element-1025" data-mp="3allla6csr14">
            <h2 className="font-display font-bold text-lg text-dark-navy" data-id="element-1026" data-mp="3bpwvbygec5w0">
              {title}
            </h2>
            {subtitle && <p className="text-[13px] text-slate-500 mt-1 leading-relaxed" data-id="element-1027" data-mp="36s3t1gi7rw10">
                {subtitle}
              </p>}
          </div>
          <button onClick={onClose} aria-label="Close" className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-dark-navy transition-colors duration-150 ease-out flex-shrink-0" data-id="element-1028" data-mp="3gozux3dlxjwl">
            <XIcon className="w-4 h-4" data-id="element-1029" data-mp="w0vbei71ll2t" />
          </button>
        </div>
        {children}
      </motion.div>
    </div>;
}

/* ------------------------------------------------------- manage categories */

interface CategoryManagerProps {
  categories: string[];
  /** How many items sit in each category right now. */
  counts: Record<string, number>;
  onClose: () => void;
  onAdd: (name: string) => void;
  onRename: (from: string, to: string) => void;
  onDelete: (name: string) => void;
  onReorder: (next: string[]) => void;
  /** How many selling recommendations each category carries, if any. */
  recoCount?: (name: string) => number;
  onOpenRecommendations?: (name: string) => void;
}

/**
 * Deliberately not a taxonomy editor. A flat, reorderable list of names — no
 * hierarchy, no ids, no schema. Talab's own AI classification happens in the
 * background and is never surfaced here.
 */
export function CategoryManager({
  categories,
  counts,
  onClose,
  onAdd,
  onRename,
  onDelete,
  onReorder,
  recoCount,
  onOpenRecommendations
}: CategoryManagerProps) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [dragged, setDragged] = useState<string | null>(null);
  const list = categories.filter(c => c !== UNCATEGORIZED);
  const taken = (n: string) => categories.some(c => c.toLowerCase() === n.trim().toLowerCase());

  /* --- delete confirmation, so nobody loses items by accident ----------- */
  if (confirmDelete) {
    const n = counts[confirmDelete] ?? 0;
    return <Sheet title={`Delete "${confirmDelete}"?`} onClose={onClose} data-id="element-1030" data-mp="1n5d3w70x8wng">
        <div className="px-7 py-6" data-id="element-1031" data-mp="1i5icm2d8i51v">
          <div className="flex items-start gap-3 px-4 py-3.5 bg-amber-50/70 border border-amber-200/70 rounded-2xl" data-id="element-1032" data-mp="1i4h342bv9qm9">
            <AlertTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1033" data-mp="1d7z1hbbu28e5" />
            <p className="text-[13px] text-amber-900 leading-relaxed" data-id="element-1034" data-mp="3d8x83098j56i">
              {n === 0 ? 'This category is empty, so nothing will change in your catalogue.' : `${n} ${n === 1 ? 'item is' : 'items are'} currently in this category. ${n === 1 ? 'It' : 'They'} will be moved to ${UNCATEGORIZED} — nothing is deleted.`}
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 mt-6" data-id="element-1035" data-mp="2uguphjd19etm">
            <button onClick={() => setConfirmDelete(null)} className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-1036" data-mp="2zha228phqiaj">
              Cancel
            </button>
            <button onClick={() => {
            onDelete(confirmDelete);
            setConfirmDelete(null);
          }} className="px-5 py-2.5 bg-error text-white text-sm font-bold rounded-xl hover:bg-red-600 transition-colors duration-150 ease-out" data-id="element-1037" data-mp="2pkdymheg8mcu">
              Delete category
            </button>
          </div>
        </div>
      </Sheet>;
  }

  /* --- add ------------------------------------------------------------- */
  if (adding) {
    const invalid = !name.trim() || taken(name);
    return <Sheet title="Add category" onClose={onClose} data-id="element-1038" data-mp="2d7js0yc6bxpn">
        <div className="px-7 py-6" data-id="element-1039" data-mp="2qzgd4iffd7cj">
          <label className="block" data-id="element-1040" data-mp="2qyf22dyeskut">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-1041" data-mp="2r1t1pefoufyk">
              Category name
            </span>
            <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="Premium SUVs" className="w-full mt-2 px-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm text-dark-navy placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-1042" data-mp="30y5xchusn3vy" />
          </label>
          {name.trim() && taken(name) && <p className="text-[12px] font-semibold text-error mt-2" data-id="element-1043" data-mp="36mo17fylz4o7">
              You already have a category with that name.
            </p>}
          <label className="block mt-4" data-id="element-1044" data-mp="3fmkgws30st74">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-1045" data-mp="3fods8kokw1lg">
              Description
              <span className="ml-1.5 font-semibold normal-case tracking-normal text-slate-300" data-id="element-1046" data-mp="3kjt4x9aunrga">
                optional
              </span>
            </span>
            <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Family and desert-ready 7-seaters" className="w-full mt-2 px-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm text-dark-navy placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-1047" data-mp="3ucruate67m0y" />
          </label>
          <div className="flex items-center justify-end gap-2 mt-6" data-id="element-1048" data-mp="haegiplmn9l8">
            <button onClick={() => {
            setAdding(false);
            setName('');
            setDesc('');
          }} className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-1049" data-mp="hdaegdxh5y9o">
              Cancel
            </button>
            <button disabled={invalid} onClick={() => {
            onAdd(name.trim());
            setAdding(false);
            setName('');
            setDesc('');
          }} className="px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-40 transition-colors duration-150 ease-out" data-id="element-1050" data-mp="102gvlwimehtd">
              Add category
            </button>
          </div>
        </div>
      </Sheet>;
  }

  /* --- the list -------------------------------------------------------- */
  return <Sheet title="Categories" subtitle="Organize your inventory so customers can browse it more easily. Drag to reorder." onClose={onClose} wide data-id="element-1051" data-mp="27ftwqf2fyh9m">
      <div className="px-7 py-5 overflow-y-auto flex-1" data-id="element-1052" data-mp="1xopdv49nac9i">
        {list.length === 0 ? <div className="py-10 text-center" data-id="element-1053" data-mp="2lea1w1knnzpn">
            <span className="w-11 h-11 rounded-2xl bg-slate-50 border border-slate-100 inline-flex items-center justify-center" data-id="element-1054" data-mp="2lfcuqf1jgc87">
              <FolderIcon className="w-5 h-5 text-slate-400" strokeWidth={1.5} data-id="element-1055" data-mp="2bgnjv6827s1g" />
            </span>
            <p className="text-sm font-bold text-dark-navy mt-3.5" data-id="element-1056" data-mp="2gfqdzvmi4dun">
              No categories yet
            </p>
            <p className="text-[13px] text-slate-500 mt-1.5 max-w-xs mx-auto leading-relaxed" data-id="element-1057" data-mp="101qfmp3b0w9i">
              Group what you sell so customers can find it faster.
            </p>
          </div> : <div className="space-y-1" data-id="element-1058" data-mp="3j7t5mkjd45we">
            {list.map(c => <div key={c} draggable={editing !== c} onDragStart={() => setDragged(c)} onDragEnd={() => setDragged(null)} onDragOver={e => {
          e.preventDefault();
          if (!dragged || dragged === c) return;
          const next = categories.filter(x => x !== dragged);
          next.splice(next.indexOf(c), 0, dragged);
          onReorder(next);
        }} className={`group flex items-center gap-3 px-3 py-3 rounded-2xl border transition-colors duration-150 ease-out ${dragged === c ? 'border-primary-indigo/40 bg-primary-indigo/[0.04]' : 'border-transparent hover:border-slate-200 hover:bg-slate-50/70'}`} data-id="element-1059" data-mp="3ja5jomm01w46">
                <GripVerticalIcon className="w-4 h-4 text-slate-300 flex-shrink-0 cursor-grab" data-id="element-1060" data-mp="2grrbhqkrv4d1" />

                {editing === c ? <>
                    <input autoFocus value={draftName} onChange={e => setDraftName(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && draftName.trim()) {
                onRename(c, draftName.trim());
                setEditing(null);
              }
              if (e.key === 'Escape') setEditing(null);
            }} className="flex-1 min-w-0 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-dark-navy outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-1061" data-mp="33fokurqqvdha" />
                    <button onClick={() => {
              if (draftName.trim()) onRename(c, draftName.trim());
              setEditing(null);
            }} className="w-8 h-8 rounded-xl bg-primary-indigo text-white flex items-center justify-center flex-shrink-0 hover:bg-indigo-700 transition-colors duration-150 ease-out" aria-label="Save name" data-id="element-1062" data-mp="21qcejhi6pnhc">
                      <CheckIcon className="w-4 h-4" data-id="element-1063" data-mp="q9rnyy2b52f0" />
                    </button>
                  </> : <>
                    <span className="text-sm font-semibold text-dark-navy truncate flex-1 min-w-0" data-id="element-1064" data-mp="3pqg9uho774p6">
                      {c}
                    </span>
                    <span className="text-[12px] text-slate-400 flex-shrink-0" data-id="element-1065" data-mp="2kp9fz2yzpoq">
                      {counts[c] ?? 0}{' '}
                      {(counts[c] ?? 0) === 1 ? 'item' : 'items'}
                    </span>
                    {recoCount && onOpenRecommendations && <button onClick={() => onOpenRecommendations(c)} className={`text-[12px] font-semibold flex-shrink-0 transition-colors duration-150 ease-out ${recoCount(c) > 0 ? 'text-ai-purple hover:text-purple-700' : 'text-slate-300 hover:text-ai-purple'}`} data-id="element-1066" data-mp="36wug1ol75thd">
                        {recoCount(c) > 0 ? `${recoCount(c)} recommendations` : 'No recommendations'}
                      </button>}
                    <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150 ease-out" data-id="element-1067" data-mp="3vbio6b400xvf">
                      <button onClick={() => {
                setEditing(c);
                setDraftName(c);
              }} aria-label={`Rename ${c}`} className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:bg-white hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-1068" data-mp="87cet85vmjo3">
                        <PencilIcon className="w-3.5 h-3.5" data-id="element-1069" data-mp="1n33z24huevzk" />
                      </button>
                      <button onClick={() => setConfirmDelete(c)} aria-label={`Delete ${c}`} className="w-8 h-8 rounded-xl flex items-center justify-center text-slate-400 hover:bg-white hover:text-error transition-colors duration-150 ease-out" data-id="element-1070" data-mp="1s4jm223kzckl">
                        <Trash2Icon className="w-3.5 h-3.5" data-id="element-1071" data-mp="25xj0e0rmn0qk" />
                      </button>
                    </div>
                  </>}
              </div>)}
          </div>}

        {(counts[UNCATEGORIZED] ?? 0) > 0 && <div className="flex items-center gap-3 px-3 py-3 mt-1 rounded-2xl bg-slate-50/70" data-id="element-1072" data-mp="2135a2crtnjsr">
            <span className="w-4 flex-shrink-0" data-id="element-1073" data-mp="2ot935q7dw15i" />
            <span className="text-sm font-semibold text-slate-500 flex-1" data-id="element-1074" data-mp="2tkr5oip229sl">
              {UNCATEGORIZED}
            </span>
            <span className="text-[12px] text-slate-400" data-id="element-1075" data-mp="15wz2p4zlpne0">
              {counts[UNCATEGORIZED]} items
            </span>
          </div>}
      </div>

      <div className="px-7 py-5 border-t border-slate-100" data-id="element-1076" data-mp="10zfo23yzr8lj">
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-dark-navy hover:border-primary-indigo/40 hover:text-primary-indigo transition-colors duration-150 ease-out" data-id="element-1077" data-mp="vzb76cu68bus">
          <PlusIcon className="w-4 h-4" data-id="element-1078" data-mp="1jp5j8s6o1wdz" />
          Add category
        </button>
      </div>
    </Sheet>;
}

/* ---------------------------------------------------------- move to category */

export function MoveToCategory({
  count,
  categories,
  onClose,
  onMove,
  onCreate
}: {
  count: number;
  categories: string[];
  onClose: () => void;
  onMove: (category: string) => void;
  onCreate: (name: string) => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const options = Array.from(new Set([...categories, UNCATEGORIZED]));
  return <Sheet title={`Move ${count} ${count === 1 ? 'item' : 'items'}`} onClose={onClose} data-id="element-1079" data-mp="2jwahx52ewkps">
      <div className="px-7 py-5 overflow-y-auto flex-1" data-id="element-1080" data-mp="2ov403asn8zwa">
        <div className="space-y-1" data-id="element-1081" data-mp="2owosatrhk6qz">
          {options.map(c => <button key={c} onClick={() => {
          setPicked(c);
          setCreating(false);
        }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border text-left transition-colors duration-150 ease-out ${picked === c ? 'border-primary-indigo bg-primary-indigo/[0.04]' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1082" data-mp="2tv02x6plula9">
              <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${picked === c ? 'border-primary-indigo' : 'border-slate-300'}`} data-id="element-1083" data-mp="3clzkhmurssti">
                {picked === c && <span className="w-2 h-2 rounded-full bg-primary-indigo" data-id="element-1084" data-mp="2mwkheu3enmnc" />}
              </span>
              <span className={`text-sm font-semibold ${c === UNCATEGORIZED ? 'text-slate-500' : 'text-dark-navy'}`} data-id="element-1085" data-mp="31l6x4wwy2gf3">
                {c}
              </span>
            </button>)}
        </div>

        {creating ? <div className="flex items-center gap-2 mt-3" data-id="element-1086" data-mp="3akahe47ul1v6">
            <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="New category name" className="flex-1 min-w-0 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-dark-navy placeholder:text-slate-300 outline-none focus:border-primary-indigo/50 focus:ring-4 focus:ring-primary-indigo/10 transition-[border-color,box-shadow] duration-150 ease-out" data-id="element-1087" data-mp="35ntpr01fw5dc" />
            <button disabled={!name.trim()} onClick={() => {
          onCreate(name.trim());
          setPicked(name.trim());
          setCreating(false);
          setName('');
        }} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-dark-navy hover:border-primary-indigo/40 disabled:opacity-40 transition-colors duration-150 ease-out flex-shrink-0" data-id="element-1088" data-mp="pemdocjj5ef">
              Create
            </button>
          </div> : <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 mt-3 px-3 py-2 text-[13px] font-bold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-1089" data-mp="3i77j4ta2bgxd">
            <PlusIcon className="w-4 h-4" data-id="element-1090" data-mp="12eruvz8hicu3" />
            Create category
          </button>}
      </div>

      <div className="flex items-center justify-end gap-2 px-7 py-5 border-t border-slate-100" data-id="element-1091" data-mp="17duydf8tgazy">
        <button onClick={onClose} className="px-4 py-2.5 text-sm font-semibold text-slate-600 hover:text-dark-navy transition-colors duration-150 ease-out" data-id="element-1092" data-mp="3qcnxnf32souf">
          Cancel
        </button>
        <button disabled={!picked} onClick={() => picked && onMove(picked)} className="px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 disabled:opacity-40 transition-colors duration-150 ease-out" data-id="element-1093" data-mp="3hisqy2mb2qco">
          Move
        </button>
      </div>
    </Sheet>;
}